import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { Attachment } from "../models/attachment";
import randomId  from "alphanumeric-id"
import { environment } from "../environments/environment";
import { AnalyticsSearchOptions } from "../pages/Analytics/analytics";
import { DailyChecklist } from "../models/daily-checklist";
import { calculateDateRange } from "./sessionSlice";
import { ExtendedAnalyticsSearchOptions } from "../components/ui/filterModalDropdown";
import { RootState } from "./store";

// Models Start
export interface ChartData {
    id: number;
    userId: number | null;
    name: string;
    description: string;
    type: string;
    group: string;
    calculation: string;
    timeIncrement: string;
    chartType: string;
    timePeriod: string;
    customStartDate: string | null;
    customEndDate: string | null;
    deleted: number;
}
// Models End

// API Calls Start
const apiGetAllAnalyticChartData = async () => {
    const baseURL = environment.apiURL;
    var params = '?fields=all';

    const response = await axios.get(`${baseURL}/chartAnalytics${params}`);
    return response.data;
}

const apiGetByChartDataByID = async (id: number) => {
    const baseURL = environment.apiURL;
    var params = '?fields=all';

    const response = await axios.get(`${baseURL}/chartAnalytics/${id}${params}`);
    return response.data;
}



const apiCreateChartData = async (chartData: ChartData) => {
    const baseURL = environment.apiURL;
    const response = await axios.post(`${baseURL}/chartAnalytics`, {
  ...chartData
    });
    return response.data;
}


const apiUpdateChartData = async (chartData: ChartData) => {
    const baseURL = environment.apiURL;
    const response = await axios.put(`${baseURL}/chartAnalytics/${chartData}`);
    return response.data;
}


const apiDeleteChartData = async (chartData: ChartData) => {
    chartData.deleted = 1;
    const baseURL = environment.apiURL;
    const response = await axios.post(`${baseURL}/chartAnalytics`, {...chartData});
    return response.data
}

const apiGetDailyChecklistUsingAnalyticsSearchOptions = async (options: AnalyticsSearchOptions, dispatch: any): Promise<DailyChecklist[]> => {
    const baseURL = environment.apiURL;
    var params = '?fields=all,location.all,audits.all,audits.supplier.all,audits.location.all,location.oem.all,audits.createUser.all,audits.part.all';

    const rangeResult = await dispatch(calculateDateRange(options.timePeriod));
    if (rangeResult.type.endsWith('/rejected')) {
        throw new Error('Failed to calculate date range');
    }
    const range = rangeResult.payload;

    if (range.after) {
        params += `&after=${options.customStartDate ?? range.after.toISOString()}`;
    }

    if (range.before) {
        params += `&before=${options.customEndDate ?? range.before.toISOString()}`;
    }

    const response = await axios.get(`${baseURL}/dailyChecklists${params}`);
    return response.data;
}
// API Calls End

// Thunks Start
export const getAllAnalyticChartData = createAsyncThunk(
    'analytics/getAllChartData',
    async () => {
        try {       
            const chartData = await apiGetAllAnalyticChartData();
            return chartData;
        } catch (error) {
            return null;
        }
    }
);

export const deleteChartData = createAsyncThunk(
    'analytics/deleteChartData',
    async (chartDataParam: ChartData, { rejectWithValue }) => {
        try {
            const chartData = await apiDeleteChartData(chartDataParam);
            return chartData;
        } catch (error) {
            return rejectWithValue('Failed to delete chart data');
        }
    }
);

export const getChartDataByID = createAsyncThunk(
    'analytics/getChartDataByID',
    async (id: number, { rejectWithValue }) => {
        try {
            const chartData = await apiGetByChartDataByID(id);
            return chartData;
        } catch (error) {
            return rejectWithValue('Failed to get chart data');
        }
    }
);

export const createChartData = createAsyncThunk(
    'analytics/createChartData',
    async (chartDataParam: ChartData, { rejectWithValue }) => {
        try {
            const chartData = await apiCreateChartData(chartDataParam);
            return chartData;
        } catch (error) {
            return rejectWithValue('Failed to create chart data');
        }
    }
);

export const updateChartData = createAsyncThunk(
    'analytics/updateChartData',
    async (chartDataParam: ChartData, { rejectWithValue }) => {
        try {
            const chartData = await apiUpdateChartData(chartDataParam);
            return chartData;
        } catch (error) {
            return rejectWithValue('Failed to update chart data');
        }
    }
);

export const getDailyChecklistUsingAnalyticsSearchOptions = createAsyncThunk(
    'analytics/getDailyChecklistUsingAnalyticsSearchOptions',
    async (options: AnalyticsSearchOptions, { dispatch }) => {
        const dailyChecklist = await apiGetDailyChecklistUsingAnalyticsSearchOptions(options, dispatch);
        return dailyChecklist;
    }
);
// Thunks End


// Slice Start
interface AnalyticsState {
  chartData: ExtendedAnalyticsSearchOptions[];
  isLoading: boolean;
  error: string | null;
}

const initialState: AnalyticsState = {
  chartData: [],
  isLoading: false,
  error: null
};

const analyticsSlice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllAnalyticChartData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getAllAnalyticChartData.fulfilled, (state, action) => {
        state.chartData = action.payload;
        state.isLoading = false;
      })
      .addCase(getAllAnalyticChartData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch chart data';
      })
      .addCase(createChartData.fulfilled, (state, action) => {
        state.chartData = [...state.chartData, action.payload];
      });
  }
});
// Slice End

// Export actions and reducer
export const {  } = analyticsSlice.actions;

// Selectors
export const selectChartData = (state: RootState) => state.analytics.chartData;
export const selectIsLoading = (state: RootState) => state.analytics.isLoading;
export const selectError = (state: RootState) => state.analytics.error;

export default analyticsSlice.reducer;
